import { z } from 'zod';
import { DiscountFormSchema } from './discountFormValidation';
import { IProduct, ICollection } from './productCollectionFormValidation';
import { defaultRule } from '../helpers/ruleHelper';
// Rule validation schema
const DealRuleSchema = z.object({
    company_category: z.string()
        .min(1, 'Company category is required'),
        
    visibility: z.boolean()
        .default(true),
        
    cost_price_upcharge_percentage: z.number()
        .min(0, 'Cost price upcharge must be non-negative')
        .max(100, 'Cost price upcharge must be less than 100%')
        .default(0),
        
    discount: z.lazy(() => DiscountFormSchema)
        .optional(),
});

// Main deal form validation schema
export const DealFormSchema = z.object({
    name: z.string()
        .min(1, 'Deal name is required')
        .max(100, 'Deal name cannot exceed 100 characters')
        .default(''),
        
    description: z.string()
        .max(500, 'Deal description cannot exceed 500 characters')
        .optional()
        .nullable(),
        
    products: z.array(
        z.object({
            id: z.number(),
            label: z.string().optional(),
            image: z.string().optional()
        })
    )
    .default([]),
    
    collections: z.array(
        z.object({
            id: z.number(),
            label: z.string().optional(),
            image: z.string().optional()
        })
    )
    .default([]),
    
    tag_rules: z.array(DealRuleSchema)
        .nullable()
        .default([defaultRule])
        .refine(
            (rules) => {
                if (!rules) return true;
                
                // Extract all company_category values
                const categories = rules.map(rule => rule.company_category);
                
                // Check for duplicates by comparing the length of the set with the original array
                const uniqueCategories = new Set(categories);
                return uniqueCategories.size === categories.length;
            },
            {
                message: "Duplicate company categories are not allowed. Each rule must have a unique company category.",
                path: ["company_category"]
            }
        ),
});

export const DealSchema = DealFormSchema.extend({
    inserted_at: z.date(),
    updated_at: z.date(),
    products: z.array(z.lazy(() => z.custom<IProduct>())),
    collections: z.array(z.lazy(() => z.custom<ICollection>()))
});

export type IDealForm = z.infer<typeof DealFormSchema>;
export type IDealRule = z.infer<typeof DealRuleSchema>;
export type IDeal = z.infer<typeof DealSchema>